import React from 'react';
import { clearFix, darken, rem } from 'polished';
import styled, { css } from 'styled-components';

import { breakpoints } from 'helpers/styles/constants';
import { circle, clearVerticalMargin } from 'helpers/styles/mixins';

interface ContentWrapperProps {
  isColumns?: boolean;
  isMainColor?: boolean;
}

const ContentWrapper = styled.div<ContentWrapperProps>`
  ${clearVerticalMargin()};
  line-height: 1.8;
  color: ${({ isMainColor, theme }) =>
    isMainColor ? theme.colors.textDark : theme.colors.textLight};
  ${({ isColumns }) =>
    isColumns &&
    css`
      margin-top: ${rem(30)};

      img {
        float: left;
        width: calc(50% + ${rem(30)});
        padding-right: ${rem(30)};
        padding-bottom: ${rem(15)};
      }

      @media ${breakpoints.smUp} {
        margin-top: ${rem(70)};
        column-count: 2;
      }
    `};

  h1 {
    margin-top: ${rem(30)};
    font-family: ${({ theme }) => theme.fontFamilies.sec};
    font-size: ${({ theme }) => theme.fontSizes.dbBig};
    font-weight: ${({ theme }) => theme.fontWeights.secNormal};
    line-height: 1.2;
  }

  h1,
  h2 {
    color: ${({ theme }) => theme.colors.textDark};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: 0;
    line-height: 1.2;
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: ${({ theme }) => theme.fontWeights.mainNormal};
  }

  h2 {
    margin-top: ${rem(26)};
    font-size: ${({ theme }) => theme.fontSizes.dbMedium};
  }

  h3 {
    margin-top: ${rem(22)};
    font-size: ${({ theme }) => theme.fontSizes.dbNormal};
  }

  h4 {
    margin-top: ${rem(16)};
    font-size: ${({ theme }) => theme.fontSizes.dbNormalSm};
  }

  h5,
  h6 {
    margin-top: ${rem(14)};
    font-size: ${({ theme }) => theme.fontSizes.dbSmall};
  }

  p {
    margin-top: ${rem(25)};

    ${({ isColumns }) => isColumns && clearFix()}
  }

  ol {
    padding-left: ${rem(40)};

    li {
      padding-left: ${rem(30)};
    }
  }

  ul {
    padding-left: ${rem(10)};
    margin-top: ${rem(25)};
    margin-bottom: 0;

    li {
      position: relative;
      padding-left: ${rem(20)};

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: ${rem(12)};
        ${circle(rem(6))};
        background-color: ${({ theme }) => theme.colors.accent};
      }
    }

    @media ${breakpoints.xsUp} {
      padding-left: ${rem(30)};

      li {
        padding-left: ${rem(40)};
      }
    }
  }

  a {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.textDark};
    transition: 0.3s ease color;

    &:hover {
      color: ${({ theme }) => darken(0.2, theme.colors.main)};
    }
  }
`;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  columns?: boolean;
  mainColor?: boolean;
}

export default function Content({ columns = false, mainColor = false, ...rest }: Props) {
  return <ContentWrapper isColumns={columns} isMainColor={mainColor} {...rest} />;
}
